import {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ChatMessageItem } from '@/components/organisms/chat/ChatMessageCache';

const useChatPlayer = (props: {
  getNextMessage: (
    key: number | undefined
  ) => [number, ChatMessageItem] | undefined;
  getPreviousMessage: (
    key: number | undefined
  ) => [number, ChatMessageItem] | undefined;
  audioOutputDeviceId?: string;
  language: string;
  userId: string;
  subUserLanguage: string;
  dualLangTranslation: boolean;
  isProjectionModeAutoPlay: boolean;
  canGuideControl: boolean;
  play: (
    text: string,
    language: string,
    messageId: string,
    voiceRate: number,
    deviceId?: string
  ) => Promise<void>;
}) => {
  const {
    getNextMessage,
    language,
    subUserLanguage,
    dualLangTranslation,
    isProjectionModeAutoPlay,
    canGuideControl,
    userId,
    play,
    audioOutputDeviceId,
    getPreviousMessage,
  } = props;

  // 音声再生関連
  const [playMessageEnabled, setPlayMessageEnabled] = useState(false);
  const [isPlayingMessage, setIsPlayingMessage] = useState(false);
  const [playedKey, setPlayedKey] = useState<number | undefined>();
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const playCounter = useRef(0);

  // 音声の自動再生処理
  useEffect(() => {
    if (playMessageEnabled && isPlayingMessage === false) {
      const timeoutId = setInterval(async () => {
        let retry = false;
        let keepPlayedKey = playedKey;
        do {
          retry = false;
          const isDualMode = canGuideControl && dualLangTranslation;
          const isPlayable = (item: ChatMessageItem) =>
            item.isRemoved === false && // 削除されていない
            (item.languages.find((x) => x.language === language)?.message ??
              '') !== '' && // 送信中以外
            (isDualMode ||
              isProjectionModeAutoPlay ||
              item.userId !== userId) && // 自分以外 or in dual mode or 投影モード自動再生
            item.userId !== '00000000-0000-0000-0000-000000000000'; // システムメッセージ

          // 続く再生可能なメッセージを取得
          let nextMsgObject = getNextMessage(keepPlayedKey);
          while (nextMsgObject != null) {
            if (isPlayable(nextMsgObject[1])) break;
            nextMsgObject = getNextMessage(nextMsgObject[0]);
          }

          if (nextMsgObject != null) {
            const [nextKey, nextMsg] = nextMsgObject;
            if (nextMsg.isTyping === false) {
              keepPlayedKey = nextKey;
              setPlayedKey(nextKey);

              if (isPlayable(nextMsg)) {
                setIsPlayingMessage(true);
                setIsAutoPlaying(true);
                const counter = playCounter.current;
                const isMine = userId === nextMsg.userId;
                if (isDualMode && isMine && !nextMsg.isSubUserSend) {
                  //play translated message in dual mode
                  const msg =
                    nextMsg.languages.find(
                      (x) => x.language === subUserLanguage
                    )?.message ?? '';
                  const voiceRate =
                    nextMsg.languages.find(
                      (x) => x.language === subUserLanguage
                    )?.voiceRate ?? 1;

                  if (msg) {
                    await play(
                      msg,
                      subUserLanguage,
                      nextMsg.id,
                      voiceRate,
                      audioOutputDeviceId
                    ); // 読み上げ
                  }
                } else {
                  const msg =
                    nextMsg.languages.find((x) => x.language === language)
                      ?.message ?? '';
                  const voiceRate =
                    nextMsg.languages.find((x) => x.language === language)
                      ?.voiceRate ?? 1;

                  await play(
                    msg,
                    language,
                    nextMsg.id,
                    voiceRate,
                    audioOutputDeviceId
                  ); // 読み上げ
                }
                if (counter === playCounter.current) {
                  // 手動で再生されていなければ再生フラグを下げる
                  setIsPlayingMessage(false);

                  // 一番最後の再生可能なメッセージを取得
                  let lastMsg = getPreviousMessage(undefined);
                  while (lastMsg != null) {
                    if (isPlayable(lastMsg[1])) break;
                    lastMsg = getPreviousMessage(lastMsg[0]);
                  }

                  // 一番最後のメッセージであれば自動再生中のフラグを下げる
                  if (nextMsg.id === lastMsg?.[1].id) {
                    setIsAutoPlaying(false);
                  }
                }
              } else {
                retry = true;
              }
            }
          }
        } while (retry);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [
    playMessageEnabled,
    playedKey,
    isPlayingMessage,
    getNextMessage,
    getPreviousMessage,
    audioOutputDeviceId,
    language,
    play,
    userId,
    canGuideControl,
    dualLangTranslation,
    isProjectionModeAutoPlay,
    subUserLanguage,
  ]);

  const setPlayMessageEnabledCallback = useCallback(
    (value: SetStateAction<boolean>) => {
      setPlayMessageEnabled(value);
      setPlayedKey(getPreviousMessage(undefined)?.[0]); // 末尾から再生
    },
    [getPreviousMessage]
  );

  const playChatCallback = useCallback(
    async (
      text: string,
      language: string,
      messageId: string,
      voiceRate: number
    ) => {
      playCounter.current++;
      const counter = playCounter.current;
      setIsPlayingMessage(true);
      setIsAutoPlaying(false);
      await play(text, language, messageId, voiceRate, audioOutputDeviceId);
      if (counter === playCounter.current) {
        setIsPlayingMessage(false);
        setPlayedKey(getPreviousMessage(undefined)?.[0]); // 手動再生終了後は末尾から再生
      }
    },
    [play, audioOutputDeviceId, getPreviousMessage]
  );

  const stopChatCallback = useCallback(async () => {
    playCounter.current++;
    setIsPlayingMessage(false);
    setIsAutoPlaying(false);
    setPlayedKey(getPreviousMessage(undefined)?.[0]); // 手動再生終了後は末尾から再生
  }, [getPreviousMessage]);

  return {
    playMessageEnabled,
    setPlayMessageEnabled: setPlayMessageEnabledCallback,
    play: playChatCallback, // マニュアル再生
    stop: stopChatCallback, // マニュアル停止
    isPlaying: isPlayingMessage,
    isAutoPlaying,
  };
};
export default useChatPlayer;
